// Die Aufloesung getippter Angaben beim Abschicken (ST-SEA-002, „PQL S1“;
// CM-SEA-012, CM-SEA-013).
//
// Stichwoerter werden ueber die Wertquelle der Suche aufgeloest (S0, POST
// /api/party-search-term-suggestions): sie liefert nur Begriffe, die die
// Facette trifft, also gibt es keinen still leeren Treffer und kein Orakel.
// Haeuser kommen aus den Suchoptionen (nur die eigenen). Genau ein exakter
// Treffer wird uebernommen; mehrdeutig und unbekannt werden ein Hinweis mit
// Knoepfen, die nur das Wort ersetzen (DESIGN, hoechstens drei).

import type { SendLike } from '../command-client.ts';
import type { SearchOptionContext } from '../parties.ts';
import { artWort, gemeinsameArt } from '../haus-art.ts';
import type { Span } from './abfrage/baum.ts';
import { printValue } from './abfrage/drucker.ts';
import { editDistance, fold } from './abfrage/felder.ts';
import type { KeywordName, S1Form } from './abfrage/s1-form.ts';
import type { HintAction, MessagePart } from './abfrage/saetze.ts';
import type { StichwortTreffer } from './stichwort-suchfeld.tsx';

export type SuggestionMatchKind = 'exact' | 'prefix' | 'contains';

export interface TermSuggestion {
  readonly taxonomyTermId: string;
  readonly displayName: string;
  readonly taxonomyDisplayName: string;
  readonly matchedName: string | null;
  readonly matchKind: SuggestionMatchKind;
}

export type TermSuggestionsResult =
  | { readonly outcome: 'loaded'; readonly suggestions: readonly TermSuggestion[]; readonly truncated: boolean }
  | { readonly outcome: 'unavailable' };

/** Die Hoechstlaenge des Suchworts, die S0 prueft (MAXIMUM_TERM_NAME_LENGTH der API); laenger antwortet 422. */
export const MAXIMUM_TERM_QUERY_LENGTH = 80;

/**
 * Die Serversuche der Stichwort-Facette ueber S0. Fehlt das Token, laeuft die
 * Anmeldung neu, wie beim Laden der Filter; sonst wiederholte das Feld nur
 * dieselbe gestoerte Anfrage.
 */
export async function searchFacetTerms(options: {
  readonly baseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly query: string;
  readonly signal: AbortSignal;
  readonly send?: SendLike | undefined;
}): Promise<StichwortTreffer> {
  const token = await options.accessToken();
  if (token === undefined) {
    options.onReauthenticate();
    return { art: 'gestoert' };
  }
  const antwort = await fetchTermSuggestions({ baseUrl: options.baseUrl, accessToken: token, query: options.query, signal: options.signal, send: options.send });
  return antwort.outcome === 'loaded'
    ? {
        art: 'geladen',
        eintraege: antwort.suggestions.map((s) => ({ id: s.taxonomyTermId, name: s.displayName, achse: s.taxonomyDisplayName, ...(s.matchedName === null ? {} : { frueher: s.matchedName }) })),
        gekuerzt: antwort.truncated,
      }
    : { art: 'gestoert' };
}

/** Die Wertquelle der Suche (S0). Das Suchwort reist im Rumpf, nie in der Adresse (ADR-0049). */
export async function fetchTermSuggestions(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly query: string;
  readonly send?: SendLike | undefined;
  /** Bricht den Abruf ab, sobald weitergetippt wird (S2b). */
  readonly signal?: AbortSignal | undefined;
}): Promise<TermSuggestionsResult> {
  const send = options.send ?? fetch;
  try {
    const response = await send(`${options.baseUrl}/party-search-term-suggestions`, {
      method: 'POST',
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
      body: JSON.stringify({ query: options.query }),
    });
    if (!response.ok) return { outcome: 'unavailable' };
    const payload = (await response.json().catch(() => undefined)) as { suggestions?: unknown; truncated?: unknown } | undefined;
    const rows = Array.isArray(payload?.suggestions) ? payload.suggestions : [];
    const suggestions = rows.flatMap((row): TermSuggestion[] => {
      const entry = row as Record<string, unknown>;
      const kind = entry.matchKind;
      return typeof entry.taxonomyTermId === 'string' &&
        typeof entry.displayName === 'string' &&
        typeof entry.taxonomyDisplayName === 'string' &&
        (kind === 'exact' || kind === 'prefix' || kind === 'contains')
        ? [
            {
              taxonomyTermId: entry.taxonomyTermId,
              displayName: entry.displayName,
              taxonomyDisplayName: entry.taxonomyDisplayName,
              matchedName: typeof entry.matchedName === 'string' ? entry.matchedName : null,
              matchKind: kind,
            },
          ]
        : [];
    });
    // truncated heisst nur „unvollstaendig“: mehr als 20 Treffer oder eine an der Tiefe abgeschnittene Nachfolgerkette.
    return { outcome: 'loaded', suggestions, truncated: payload?.truncated === true };
  } catch {
    return { outcome: 'unavailable' };
  }
}

/** Der Name eines Stichworts, wie der Chip ihn zeigt, mit Achse fuer den Rueckweg als Suchtext. */
export interface KnownTermName {
  readonly name: string;
  readonly axis: string;
}

export interface ResolvedS1 {
  readonly freeText: string | undefined;
  readonly taxonomyTermIds: readonly string[];
  readonly termNames: Readonly<Record<string, KnownTermName>>;
  readonly operatingContextId: string | undefined;
  readonly roleCodes: readonly string[];
  readonly postalCodePrefix: string | undefined;
  readonly locality: string | undefined;
  readonly excludeInactive: boolean;
}

export interface ResolutionHint {
  readonly span: Span;
  readonly parts: readonly MessagePart[];
  readonly actions: readonly HintAction[];
}

export type ResolutionResult =
  | { readonly outcome: 'resolved'; readonly resolved: ResolvedS1 }
  | { readonly outcome: 'hint'; readonly hint: ResolutionHint }
  | { readonly outcome: 'unavailable' };

const MAXIMUM_ACTIONS = 3;

const text = (value: string): MessagePart => ({ kind: 'text', text: value });
const input = (value: string): MessagePart => ({ kind: 'input', text: value });

function keywordSpelling(name: string, axis: string): string {
  return printValue(`${name} (${axis})`);
}

function resolveKeyword(keyword: KeywordName, suggestions: readonly TermSuggestion[]): { readonly term: TermSuggestion } | { readonly hint: ResolutionHint } {
  const span = keyword.span ?? { from: 0, to: 0 };
  const exact = suggestions.filter(
    (suggestion) =>
      suggestion.matchKind === 'exact' && (keyword.axis === undefined || fold(suggestion.taxonomyDisplayName) === fold(keyword.axis)),
  );
  if (exact.length === 1) return { term: exact[0] as TermSuggestion };
  if (exact.length > 1) {
    return {
      hint: {
        span,
        parts: [input(keyword.name), text(' gibt es mehrfach. Wähle, welches gemeint ist.')],
        actions: exact.slice(0, MAXIMUM_ACTIONS).map((suggestion) => ({
          label: `${suggestion.displayName} (${suggestion.taxonomyDisplayName}) übernehmen`,
          span,
          replacement: keywordSpelling(suggestion.displayName, suggestion.taxonomyDisplayName),
        })),
      },
    };
  }
  const first = suggestions[0];
  if (first === undefined) {
    return { hint: { span, parts: [text('Kein Stichwort heißt '), input(keyword.name), text('. Prüfe die Schreibweise oder suche ohne Stichwort.')], actions: [] } };
  }
  return {
    hint: {
      span,
      parts: [text('Kein Stichwort heißt '), input(keyword.name), text('. Meintest du '), input(first.displayName), text('?')],
      actions: [{ label: `${first.displayName} übernehmen`, span, replacement: printValue(first.displayName) }],
    },
  };
}

/**
 * Der Satzanfang, wenn kein eigenes Haus so heisst (M4/M7): nach der Art der
 * eigenen Haeuser, bei gemischter Art "Gesellschaften"; wer nur ein Haus hat,
 * hoert "Dein Verlag heißt nicht", und der Filter hilft dann nicht weiter.
 */
function keinHausHeisst(houses: readonly SearchOptionContext[]): { readonly anfang: string; readonly einzeln: boolean; readonly pronomen: string } {
  if (houses.length === 1) {
    const dein = artWort(houses[0]?.kind, 'dein');
    return { anfang: `${dein.charAt(0).toLocaleUpperCase('de-DE')}${dein.slice(1)} heißt nicht `, einzeln: true, pronomen: '' };
  }
  const art = gemeinsameArt(houses.map((house) => house.kind));
  if (art === 'gemischt') return { anfang: 'Keine deiner Gesellschaften heißt ', einzeln: false, pronomen: 'sie' };
  const feminin = art === 'holding';
  return { anfang: `${feminin ? 'Keine' : 'Keiner'} deiner ${artWort(art, 'plural')} heißt `, einzeln: false, pronomen: feminin ? 'sie' : 'ihn' };
}

function resolveHouse(name: string, span: Span, houses: readonly SearchOptionContext[]): { readonly id: string } | { readonly hint: ResolutionHint } {
  const folded = fold(name);
  const match = houses.filter((house) => fold(house.displayName) === folded);
  if (match.length === 1) return { id: (match[0] as SearchOptionContext).operatingContextId };
  const nearest = [...houses]
    .map((house) => ({ house, distance: fold(house.displayName).includes(folded) ? 0 : editDistance(folded, fold(house.displayName)) }))
    .sort((a, b) => a.distance - b.distance)[0];
  const satz = keinHausHeisst(houses);
  return nearest === undefined || nearest.distance > 3
    ? { hint: { span, parts: [text(satz.anfang), input(name), text(satz.einzeln ? '.' : `. Wähle ${satz.pronomen} im Filter.`)], actions: [] } }
    : {
        hint: {
          span,
          parts: [text(satz.anfang), input(name), text('. Meintest du '), input(nearest.house.displayName), text('?')],
          actions: [{ label: `${nearest.house.displayName} übernehmen`, span, replacement: printValue(nearest.house.displayName) }],
        },
      };
}

/**
 * Loest eine S1-Form auf. Stichwoerter werden einzeln nachgeschlagen; scheitert
 * die Wertquelle, ist das Ergebnis `unavailable` und die Eingabe bleibt stehen.
 * Der erste Hinweis gewinnt; der Bildschirm zeigt ihn mit dem Cursor an der Stelle.
 */
export async function resolveS1Form(
  form: S1Form,
  dependencies: {
    readonly suggestions: (query: string) => Promise<TermSuggestionsResult>;
    readonly houses: () => Promise<readonly SearchOptionContext[] | undefined>;
  },
): Promise<ResolutionResult> {
  const taxonomyTermIds: string[] = [];
  const termNames: Record<string, KnownTermName> = {};
  for (const keyword of form.keywords) {
    const result = await dependencies.suggestions(keyword.name);
    if (result.outcome === 'unavailable') return { outcome: 'unavailable' };
    const resolved = resolveKeyword(keyword, result.suggestions);
    if ('hint' in resolved) return { outcome: 'hint', hint: resolved.hint };
    if (!taxonomyTermIds.includes(resolved.term.taxonomyTermId)) taxonomyTermIds.push(resolved.term.taxonomyTermId);
    termNames[resolved.term.taxonomyTermId] = { name: resolved.term.displayName, axis: resolved.term.taxonomyDisplayName };
  }
  let operatingContextId: string | undefined;
  if (form.houseName !== undefined) {
    const houses = await dependencies.houses();
    if (houses === undefined) return { outcome: 'unavailable' };
    const resolved = resolveHouse(form.houseName, form.houseSpan ?? { from: 0, to: 0 }, houses);
    if ('hint' in resolved) return { outcome: 'hint', hint: resolved.hint };
    operatingContextId = resolved.id;
  }
  return {
    outcome: 'resolved',
    resolved: {
      freeText: form.freeText,
      taxonomyTermIds,
      termNames,
      operatingContextId,
      roleCodes: form.roleCodes,
      postalCodePrefix: form.postalCodePrefix,
      locality: form.locality,
      excludeInactive: form.excludeInactive,
    },
  };
}
